import { useCallback } from 'react';

import { useAppDispatch } from 'src/app/store/hooks';
import type {
  InvoiceRead} from 'src/common/external/bambi-api/bambiApi';
import {
  useInvoicesPaymentLinkCreateMutation,
  useInvoicesPaymentLinkListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import {
  setEditingPaymentLinkId,
  setShowPaymentLinkModal,
} from '../../invoice.slice';

type FormData = {
  payerType: 'payer_organization' | 'passenger';
  payerId: string;
};

export function usePaymentLink(invoice: InvoiceRead | null, skipRetrieve = false) {
  const dispatch = useAppDispatch();
  const [createPaymentLink, { isLoading: isCreatingPaymentLink }] =
    useInvoicesPaymentLinkCreateMutation();

  const { isLoading, data } = useInvoicesPaymentLinkListQuery(
    {
      invoice: invoice?.id,
    },
    {
      skip: !invoice || skipRetrieve,
    }
  );

  const handleCreatePaymentLink = useCallback(
    async (formData: FormData) => {
      if (!invoice) {
        return;
      }

      try {
        const result = await createPaymentLink({
          invoicePaymentLink: {
            invoice: invoice.id,
            by_passenger:
              formData.payerType === 'passenger' ? formData.payerId : null,
            by_payer:
              formData.payerType === 'payer_organization'
                ? formData.payerId
                : null,
          },
        }).unwrap();

        dispatch(setEditingPaymentLinkId(result.id));
        dispatch(setShowPaymentLinkModal(true));
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to create payment link',
            description: formatServerError(e),
          })
        );
      }
    },
    [createPaymentLink, dispatch, invoice]
  );

  const handleShowPaymentLink = useCallback(
    (formData: FormData) => {
      if (isLoading) {
        return;
      }

      const lastValidPaymentLink = data?.results.find(
        (link) => !link.is_expired
      );

      // Open existing
      if (lastValidPaymentLink) {
        dispatch(setEditingPaymentLinkId(lastValidPaymentLink.id));
        dispatch(setShowPaymentLinkModal(true));
        return;
      }

      // Create new link
      handleCreatePaymentLink(formData);
    },
    [data?.results, dispatch, handleCreatePaymentLink, isLoading]
  );

  return {
    isLoading: isLoading || isCreatingPaymentLink,
    showPaymentLink: handleShowPaymentLink,
    createPaymentLink: handleCreatePaymentLink,
  };
}
